import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Upload, Sparkles, AlertTriangle, CheckCircle2, FileText } from "lucide-react";

const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");

function Stat({ l, v, extra }) {
  return <div className="rounded-lg bg-slate-50 border px-2.5 py-1.5"><div className="text-[10px] uppercase font-bold text-slate-500">{l}</div><div className="font-heading font-black text-lg leading-tight">{v}{extra ? <span className="text-xs font-semibold text-emerald-700 ml-1">{extra}</span> : null}</div></div>;
}

// Iter 111: import del Word A3 → anteprima (doppia lettura parser + IA) → applica
export default function ImportA3Dialog({ open, onClose, ai = { enabled: true } }) {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [prev, setPrev] = useState(null);
  const [escl, setEscl] = useState(new Set());
  const [dec, setDec] = useState({}); // Iter 113: {nome: {azione, ditta, target}}
  const [persone, setPersone] = useState([]); // elenco dipendenti+esterni per "è in realtà…"
  const [batch, setBatch] = useState(null); // Iter 112: import multi-file {tot, done:[{name,ok,msg}], current}
  const [useAi, setUseAi] = useState(true);
  const aiOn = useAi && ai.enabled !== false;

  const reset = () => { setPrev(null); setEscl(new Set()); setDec({}); setBatch(null); if (fileRef.current) fileRef.current.value = ""; };
  const loadPersone = async () => {
    try {
      const [{ data: i }, { data: e }] = await Promise.all([api.get("/risorse-personale/interni"), api.get("/risorse/esterni")]);
      setPersone([...i.map((x) => ({ v: `interno|${x.id}`, l: `${x.cognome} ${x.nome} (dipendente)` })), ...e.map((x) => ({ v: `esterno|${x.id}`, l: `${x.nome} (${x.ditta || "esterno"})` }))].sort((a, b) => a.l.localeCompare(b.l)));
    } catch (_) { /* ignore */ }
  };

  // Più file insieme: ogni giorno viene letto e applicato in sequenza (nomi sconosciuti → esterni "da verificare")
  const uploadMany = async (files) => {
    setBusy(true);
    const done = [];
    setBatch({ tot: files.length, done, current: files[0].name });
    for (const f of files) {
      setBatch({ tot: files.length, done: [...done], current: f.name });
      try {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/programma/import-a3/preview", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000, params: { use_ai: aiOn } });
        const { data: r } = await api.post(`/programma/import-a3/apply/${data.id}`, { escludi_nomi: [] });
        done.push({ name: f.name, ok: true, msg: `${fmtD(r.data)} · ${r.stats.assegnazioni} assegnazioni · ${r.stats.assenze_create} assenze · ${data.riepilogo.persone_nuove} nomi nuovi` });
      } catch (e) {
        done.push({ name: f.name, ok: false, msg: e?.response?.data?.detail || "errore" });
      }
    }
    setBatch({ tot: files.length, done: [...done], current: null });
    setBusy(false);
    toast.success(`Import completato: ${done.filter((d) => d.ok).length}/${files.length} file`);
  };

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/programma/import-a3/preview", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000, params: { use_ai: aiOn } });
      setPrev(data);
      setEscl(new Set());
      setDec(Object.fromEntries(data.persone.filter((p) => !p.match).map((p) => [p.nome, { azione: p.kind === "oggetto" ? (p.suggerito || "mezzo_cantiere") : p.kind === "interno" ? "chiedi" : "esterno", ditta: p.ditta || "", target: "" }])));
      if (data.persone.some((p) => !p.match)) loadPersone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lettura del file non riuscita"); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    const indecisi = Object.entries(dec).filter(([, d]) => d.azione === "chiedi" || (d.azione === "alias" && !d.target));
    if (indecisi.length) return toast.error(`Decidi cosa fare con: ${indecisi.map(([n]) => n).join(", ")}`);
    setBusy(true);
    try {
      const decisioni = Object.entries(dec).map(([nome, d]) => ({ nome, azione: d.azione, ditta: d.ditta || "", target_tipo: d.target ? d.target.split("|")[0] : "", target_id: d.target ? d.target.split("|")[1] : "" }));
      const { data } = await api.post(`/programma/import-a3/apply/${prev.id}`, { escludi_nomi: [...escl], decisioni });
      toast.success(`Programma del ${fmtD(data.data)} importato: ${data.stats.assegnazioni} assegnazioni, ${data.stats.assenze_create} assenze, ${data.stats.esterni_creati + data.stats.mezzi_creati + data.stats.mezzi_cantiere_creati + data.stats.attrezzature_create} nuove risorse`, { duration: 9000 });
      onClose(); reset();
      nav(`/programma?data=${data.data}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import non riuscito"); }
    finally { setBusy(false); }
  };

  const toggle = (nome) => setEscl((s) => { const n = new Set(s); n.has(nome) ? n.delete(nome) : n.add(nome); return n; });
  const nonAbbinati = prev?.persone.filter((p) => !p.match) || [];
  const r = prev?.riepilogo;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) { onClose(); reset(); } }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="import-a3-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#0044FF]" /> Importa programma A3 da Word</DialogTitle>
          <DialogDescription>Il file viene letto dal lettore automatico{ai.enabled !== false ? " e dall'intelligenza artificiale" : ""}, poi ti mostro cosa cambierà: <b>niente viene salvato finché non confermi</b>.</DialogDescription>
        </DialogHeader>

        {!prev && (
          <label className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-10 text-center cursor-pointer ${busy ? "border-slate-200 bg-slate-50" : "border-[#0044FF]/40 hover:bg-blue-50/40"}`} data-testid="import-a3-dropzone">
            <input ref={fileRef} type="file" accept=".docx,.doc" multiple className="hidden" disabled={busy} onChange={(e) => { const fs = Array.from(e.target.files || []); if (fs.length > 1) uploadMany(fs); else upload(fs[0]); }} data-testid="import-a3-file" />
            {busy && !batch ? <><Loader2 className="h-7 w-7 animate-spin text-[#0044FF]" /><div className="text-sm font-semibold">Lettura del foglio in corso… (fino a 1 minuto)</div></> : !batch && <><Upload className="h-7 w-7 text-[#0044FF]" /><div className="text-sm font-semibold">Scegli il file Word del giorno (es. <span className="font-mono">36.4 GIOVEDÌ 03-09-26.docx</span>)</div><div className="text-xs text-slate-500">Puoi selezionare <b>più file insieme</b> per ricostruire lo storico: verranno importati uno dopo l'altro. Preferisci .docx. La data viene letta dal nome del file.</div></>}
            {batch && (
              <div className="w-full text-left space-y-1" data-testid="import-a3-batch">
                <div className="text-sm font-bold">{batch.current ? `Importo ${batch.done.length + 1}/${batch.tot}: ${batch.current}` : `Completato ${batch.done.filter((d) => d.ok).length}/${batch.tot}`}</div>
                {batch.current && <Loader2 className="h-5 w-5 animate-spin text-[#0044FF]" />}
                <ul className="text-xs space-y-0.5 max-h-48 overflow-y-auto">{batch.done.map((d, i) => <li key={i} className={d.ok ? "text-emerald-700" : "text-red-700"}>{d.ok ? "✓" : "✗"} <b>{d.name}</b> — {d.msg}</li>)}</ul>
              </div>
            )}
          </label>
        )}
        {!prev && !batch && (
          ai.enabled !== false ? (
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer"><input type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)} data-testid="import-a3-use-ai" /> Usa anche la lettura IA (più precisa: coglie meglio assenze e date, ~40 s per file){ai.limit ? ` · ${ai.used}/${ai.limit} usate questo mese` : ""}</label>
          ) : (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="import-a3-ai-off">Lettura IA non disponibile: {ai.reason || "inclusa solo nel piano Pro + IA"}. L'import funziona comunque con il lettore automatico standard.</div>
          )
        )}

        {prev && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-xl bg-slate-900 text-white px-3 py-2">
              <FileText className="h-4 w-4" /><span className="text-sm font-bold truncate flex-1">{prev.filename}</span>
              <span className="text-xs">Programma del <b>{fmtD(prev.data)}</b></span>
              <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${prev.ia ? "bg-emerald-500" : "bg-amber-500 text-slate-900"}`}>{prev.ia ? "IA ✓" : "senza IA"}</span>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="import-a3-riepilogo">
              <Stat l="Sedi" v={r.sedi} extra={r.sedi_nuove ? `+${r.sedi_nuove}` : ""} />
              <Stat l="Persone" v={r.persone} extra={`${r.persone_abbinate} ok`} />
              <Stat l="Assenze" v={r.assenze} />
              <Stat l="Mezzi" v={r.mezzi} extra={r.mezzi_nuovi ? `+${r.mezzi_nuovi}` : ""} />
              <Stat l="Mezzi cant." v={r.mezzi_cantiere} extra={r.mezzi_cantiere_nuovi ? `+${r.mezzi_cantiere_nuovi}` : ""} />
              <Stat l="Attrezz." v={r.attrezzature} extra={r.attrezzature_nuove ? `+${r.attrezzature_nuove}` : ""} />
            </div>
            {prev.warnings.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-1" data-testid="import-a3-warnings">
                {prev.warnings.map((w, i) => <div key={i} className="flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{w}</div>)}
              </div>
            )}
            {nonAbbinati.length > 0 ? (
              <div className="rounded-xl border border-amber-300 bg-amber-50/40 p-3" data-testid="import-a3-nuovi">
                <div className="text-xs font-bold uppercase text-amber-900 mb-1.5">Nomi non riconosciuti — dimmi chi sono (spesso è una persona già in anagrafica scritta diversamente)</div>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {nonAbbinati.map((p) => {
                    const d = dec[p.nome] || { azione: "chiedi", ditta: "", target: "" };
                    const set = (patch) => setDec((o) => ({ ...o, [p.nome]: { ...d, ...patch } }));
                    return (
                      <div key={p.nome} className="rounded-lg bg-white border px-2.5 py-2 text-sm" data-testid={`import-a3-dec-${p.nome.replace(/\s+/g, "-")}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-bold">{p.nome}</span>
                          <span className="text-xs text-slate-500">{p.kind === "oggetto" ? `sembra un ${p.suggerito === "mezzo_cantiere" ? "mezzo di cantiere" : p.suggerito === "mezzo" ? "mezzo" : "attrezzatura"}, non una persona` : p.azione === "verifica" ? "salvato come esterno DA VERIFICARE" : p.ditta ? `ditta ${p.ditta}` : p.kind === "interno" ? "scritto come dipendente" : "esterno"} · {p.blocco || p.ubicazione || "—"}</span>
                        </div>
                        {p.suggerimenti?.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="import-a3-suggerimenti">
                            <span className="text-[11px] font-semibold text-emerald-800">Forse è:</span>
                            {p.suggerimenti.map((sg) => (
                              <button key={sg.id} type="button" onClick={() => set({ azione: "alias", target: `interno|${sg.id}` })}
                                className={`rounded-full px-2.5 py-0.5 text-xs font-bold border ${d.target === `interno|${sg.id}` ? "bg-emerald-600 text-white border-emerald-600" : "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100"}`}
                                data-testid={`import-a3-sugg-${sg.id}`}>
                                {sg.label}
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {[["alias", "È in realtà…"], ["dipendente", "Nuovo dipendente"], ["esterno", "Esterno / ditta"], ["mezzo_cantiere", "Mezzo di cantiere"], ["attrezzatura", "Attrezzatura"], ["mezzo", "Mezzo"], ["salta", "Ignora"]].map(([v, l]) => (
                            <button key={v} type="button" onClick={() => set({ azione: v })} className={`rounded-full px-2.5 py-0.5 text-xs font-semibold border ${d.azione === v ? "bg-[#0044FF] text-white border-[#0044FF]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0044FF]"}`} data-testid={`import-a3-dec-${v}`}>{l}</button>
                          ))}
                          {d.azione === "alias" && (
                            <select value={d.target} onChange={(e) => set({ target: e.target.value })} className="h-7 rounded border text-xs px-1 max-w-[260px]" data-testid="import-a3-alias-target">
                              <option value="">— scegli la persona —</option>
                              {persone.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                            </select>
                          )}
                          {d.azione === "esterno" && <input value={d.ditta} onChange={(e) => set({ ditta: e.target.value })} placeholder="Ditta / agenzia" className="h-7 rounded border text-xs px-2 w-40" data-testid="import-a3-ditta" />}
                        </div>
                        {d.azione === "alias" && <div className="text-[10px] text-slate-500 mt-1">Il nome verrà salvato come alias: la prossima volta sarà riconosciuto da solo.</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : <div className="flex items-center gap-2 text-sm text-emerald-700 font-semibold"><CheckCircle2 className="h-4 w-4" /> Tutte le persone del foglio sono state riconosciute in anagrafica</div>}
            {prev.assenze.length > 0 && (
              <details className="text-xs"><summary className="cursor-pointer font-bold text-slate-600">Assenze rilevate ({prev.assenze.length})</summary>
                <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">{prev.assenze.map((a, i) => <li key={i}><b>{a.match?.label || a.nome}</b> · {a.tipo} dal {fmtD(a.dal)}{a.al ? ` al ${fmtD(a.al)}` : " (da definire)"}</li>)}</ul>
              </details>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {prev && <Button variant="outline" onClick={reset} disabled={busy} data-testid="import-a3-reset">Altro file</Button>}
          <Button variant="outline" onClick={() => { onClose(); reset(); }} disabled={busy}>Annulla</Button>
          {prev && <Button onClick={apply} disabled={busy} className="bg-[#0044FF] hover:bg-[#0033CC]" data-testid="import-a3-apply">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}Applica al {fmtD(prev.data)}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
